const baseURL = process.env.REACT_APP_API_BASE_URL;

export type WebsiteOperationStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'completed_with_errors'
  | 'exhausted'
  | 'failed'
  | 'cancelled';

export type WebsiteDiscoveredResourceKind = 'html' | 'pdf';
export type WebsiteResourceVerification =
  | 'verified'
  | 'unverified'
  | 'rejected';

export interface WebsiteCrawlResponse {
  id: number;
  status: WebsiteOperationStatus;
  cancel_requested_at?: string | null;
  submitted_url: string;
  origin?: string;
  counts?: {
    total?: number;
    html?: number;
    pdf?: number;
    excluded?: number;
  };
  discovered_html_count?: number;
  discovered_pdf_count?: number;
  excluded_count?: number;
  error_message?: string;
  created_at?: string;
  completed_at?: string | null;
}

export interface WebsiteDiscoveredResource {
  id: number;
  kind: WebsiteDiscoveredResourceKind;
  kind_hint?: WebsiteDiscoveredResourceKind;
  verification_state?: WebsiteResourceVerification;
  submitted_url?: string;
  discovered_url: string;
  effective_url?: string | null;
  canonical_hint_url?: string | null;
  title?: string;
  depth?: number;
  observed_size_bytes?: number | null;
  observed_content_type?: string | null;
  exclusion_class?: string | null;
  estimated_token_count?: number | null;
}

export interface WebsiteDiscoveredResourceListResponse {
  results: WebsiteDiscoveredResource[];
  count: number;
  page?: number;
  page_size?: number;
  total_pages?: number;
  next: string | null;
  previous: string | null;
}

export interface WebsiteImportItemResponse {
  id: number;
  resource_id: number;
  phase: string;
  result?: string;
  source_file_id?: number | null;
  error_message?: string;
}

export interface WebsiteImportJobResponse {
  id: number;
  status: WebsiteOperationStatus;
  cancel_requested_at?: string | null;
  counts?: {
    total?: number;
    activated?: number;
    failed?: number;
    cancelled?: number;
  };
  total_count?: number;
  completed_count?: number;
  failed_count?: number;
  cancelled_count?: number;
  items?: WebsiteImportItemResponse[];
  error_message?: string;
  created_at?: string;
}

export interface WebsiteOperationConflictResponse {
  detail?: string;
  operation_url?: string;
}

export const normalizeWebsiteCrawl = (
  crawl: WebsiteCrawlResponse,
): WebsiteCrawlResponse => ({
  ...crawl,
  discovered_html_count: crawl.discovered_html_count ?? crawl.counts?.html ?? 0,
  discovered_pdf_count: crawl.discovered_pdf_count ?? crawl.counts?.pdf ?? 0,
  excluded_count: crawl.excluded_count ?? crawl.counts?.excluded ?? 0,
});

export const normalizeWebsiteImportJob = (
  job: WebsiteImportJobResponse,
): WebsiteImportJobResponse => ({
  ...job,
  total_count: job.total_count ?? job.counts?.total ?? job.items?.length ?? 0,
  completed_count: job.completed_count ?? job.counts?.activated ?? 0,
  failed_count: job.failed_count ?? job.counts?.failed ?? 0,
  cancelled_count: job.cancelled_count ?? job.counts?.cancelled ?? 0,
  items: Array.isArray(job.items) ? job.items : [],
});

export interface WebsiteRecrawlJobResponse {
  id: number;
  status: WebsiteOperationStatus;
  cancel_requested_at?: string | null;
  result?:
    | 'unchanged'
    | 'activated'
    | 'deferred_conflict'
    | 'failed'
    | 'cancelled';
  source_file_id?: number | null;
  error_message?: string;
  force_reextract?: boolean;
}

// Get stored access token
export function getAccessToken(): string | null {
  return localStorage.getItem('access_token');
}

// Get stored refresh token
export function getRefreshToken(): string | null {
  return localStorage.getItem('refresh_token');
}

// Clear tokens (logout)
export function clearTokens(): void {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user_info');
  localStorage.removeItem('currentStoreId');
}

// Refresh access token when it expires
export async function refreshAccessToken(): Promise<string | null> {
  const refresh = getRefreshToken();
  if (!refresh) {
    clearTokens();
    window.location.href = '/signin';
    return null;
  }

  try {
    const response = await fetch(`${baseURL}/api/user/token/refresh/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refresh: refresh,
      }),
    });

    if (response.ok) {
      const data = await response.json();
      localStorage.setItem('access_token', data.access);
      return data.access;
    } else {
      // Refresh token is invalid, logout
      clearTokens();
      window.location.href = '/signin';
      return null;
    }
  } catch (error) {
    console.error('Token refresh error:', error);
    clearTokens();
    window.location.href = '/signin';
    return null;
  }
}

// Get current store ID
export function getCurrentStoreId(): string | null {
  return localStorage.getItem('currentStoreId');
}

export type ApiRequestOptions = RequestInit & {
  skipStoreHeader?: boolean;
  storeGroupId?: number | string;
  /**
   * Explicit X-Store-ID. `undefined` keeps the legacy behaviour (read
   * localStorage.currentStoreId unless skipStoreHeader). `null` sends no
   * X-Store-ID at all. A value is sent as-is and never falls back to
   * localStorage.
   */
  storeId?: number | string | null;
};

export function resolveStoreIdHeader(
  options: Pick<ApiRequestOptions, 'skipStoreHeader' | 'storeId'>,
): string | null {
  if (options.storeId !== undefined) {
    return options.storeId === null ? null : String(options.storeId);
  }
  return options.skipStoreHeader ? null : getCurrentStoreId();
}

// Make authenticated API requests with automatic token refresh
export async function apiRequest(
  url: string,
  options: ApiRequestOptions = {},
): Promise<Response | null> {
  const {
    skipStoreHeader = false,
    storeGroupId,
    storeId: explicitStoreId,
    ...fetchOptions
  } = options;
  let token = getAccessToken();

  if (!token) {
    clearTokens();
    // In Jest/jsdom, navigation isn't implemented and will throw.
    // In the real app, we do want to redirect to sign-in.
    const isTestEnv =
      typeof process !== 'undefined' &&
      (process.env.NODE_ENV === 'test' || !!process.env.JEST_WORKER_ID);
    if (!isTestEnv) {
      window.location.href = '/signin';
    }
    return null;
  }

  const storeId = resolveStoreIdHeader({
    skipStoreHeader,
    storeId: explicitStoreId,
  });

  // Add tenant selectors when supplied. Defer Content-Type until after inspecting the body.
  const headers: Record<string, any> = {
    Authorization: `Bearer ${token}`,
    ...(storeId && { 'X-Store-ID': storeId }),
    ...(storeGroupId !== undefined &&
      storeGroupId !== null && {
        'X-Store-Group-ID': String(storeGroupId),
      }),
    ...(fetchOptions.headers || {}),
  };
  // If the caller didn't provide Content-Type and the body is NOT FormData, default to JSON.
  // For FormData, let the browser set the proper multipart boundary.
  const bodyIsFormData =
    typeof FormData !== 'undefined' && fetchOptions.body instanceof FormData;
  if (!headers['Content-Type'] && !bodyIsFormData) {
    headers['Content-Type'] = 'application/json';
  }

  try {
    let response = await fetch(url, {
      ...fetchOptions,
      headers,
    });

    // If token expired (401), try to refresh
    if (response.status === 401) {
      console.log('Token expired, refreshing...');
      token = await refreshAccessToken();

      if (token) {
        // Retry with new token
        headers['Authorization'] = `Bearer ${token}`;
        response = await fetch(url, {
          ...fetchOptions,
          headers,
        });
      } else {
        return null; // User was logged out
      }
    }

    return response;
  } catch (error) {
    console.error('API request error:', error);
    throw error;
  }
}

// Convenience methods for common HTTP operations
export const api = {
  get: async (url: string, options?: ApiRequestOptions) => {
    return apiRequest(url, { ...options, method: 'GET' });
  },

  post: async (url: string, data?: any, options?: ApiRequestOptions) => {
    return apiRequest(url, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  patch: async (url: string, data?: any, options?: ApiRequestOptions) => {
    return apiRequest(url, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  put: async (url: string, data?: any, options?: ApiRequestOptions) => {
    return apiRequest(url, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  delete: async (url: string, options?: ApiRequestOptions) => {
    return apiRequest(url, { ...options, method: 'DELETE' });
  },
  // Submit FormData (multipart) while preserving auth and token refresh behavior
  patchForm: async (
    url: string,
    formData: FormData,
    options?: ApiRequestOptions,
  ) => {
    return apiRequest(url, {
      ...options,
      method: 'PATCH',
      body: formData,
      // Do not set Content-Type; apiRequest will omit it for FormData
    });
  },
  postForm: async (
    url: string,
    formData: FormData,
    options?: ApiRequestOptions,
  ) => {
    return apiRequest(url, {
      ...options,
      method: 'POST',
      body: formData,
    });
  },
};

// Public (unauthenticated) API convenience
export const apiPublic = {
  get: async (url: string, options?: RequestInit) => {
    return fetch(url, {
      ...options,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(options?.headers || {}),
      },
    });
  },
  post: async (url: string, data?: any, options?: RequestInit) => {
    return fetch(url, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(options?.headers || {}),
      },
      body: data ? JSON.stringify(data) : undefined,
    });
  },
  postForm: async (url: string, formData: FormData, options?: RequestInit) => {
    return fetch(url, {
      ...options,
      method: 'POST',
      body: formData,
    });
  },
  patch: async (url: string, data?: any, options?: RequestInit) => {
    return fetch(url, {
      ...options,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(options?.headers || {}),
      },
      body: data ? JSON.stringify(data) : undefined,
    });
  },
  delete: async (url: string, options?: RequestInit) => {
    return fetch(url, { ...options, method: 'DELETE' });
  },
};

// Helper function to fetch recent orders (last N hours)
export async function fetchRecentOrders(
  hoursAgo: number = 2,
  limit: number = 10,
) {
  try {
    const response = await api.get(apiEndpoints.fetchOrdersByStatus('All'));

    if (!response || !response.ok) {
      throw new Error('Failed to fetch orders');
    }

    const data = await response.json();

    if (!Array.isArray(data)) {
      return [];
    }

    // Filter orders from last N hours
    const timeAgo = new Date();
    timeAgo.setHours(timeAgo.getHours() - hoursAgo);

    const recentOrders = data.filter((order: any) => {
      const orderDate = new Date(order.created_at);
      return orderDate >= timeAgo;
    });

    // Sort by most recent first and limit
    return recentOrders
      .sort(
        (a: any, b: any) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      )
      .slice(0, limit);
  } catch (error) {
    console.error('Error fetching recent orders:', error);
    throw error;
  }
}

// Specific API endpoints
export const apiEndpoints = {
  // User endpoints
  getUserInfo: () => `${baseURL}/api/user-info/`,
  getStoreUserInfo: () => `${baseURL}/api/stores/user-info/`,
  dismissAnnouncement: (key: string) =>
    `${baseURL}/api/stores/user-info/announcements/${encodeURIComponent(key)}/dismiss/`,

  // Order endpoints
  getOrders: (
    page?: number,
    pageSize?: number,
    status?: string,
    ordering?: string,
    search?: string,
  ) => {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (pageSize) params.append('page_size', pageSize.toString());
    if (status && status !== 'All') params.append('status', status);
    if (ordering) params.append('ordering', ordering);
    if (search) params.append('q', search);
    const query = params.toString();
    return `${baseURL}/api/orders/${query ? `?${query}` : ''}`;
  },
  getLegacyOrders: () => `${baseURL}/api/order/`,
  fetchOrdersByStatus: (status: string) =>
    `${baseURL}/api/fetch-order/${status}/`,
  updateOrder: (orderId: string) => `${baseURL}/api/order/${orderId}/`,
  getOrdersCount: () => `${baseURL}/api/orders/count/`,
  getAdminOrders: (
    page?: number,
    pageSize?: number,
    status?: string,
    ordering?: string,
    q?: string,
  ) => {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (pageSize) params.append('page_size', pageSize.toString());
    if (status && status !== 'All') params.append('status', status);
    if (ordering) params.append('ordering', ordering);
    if (q) params.append('q', q);
    const query = params.toString();
    return `${baseURL}/api/orders/admin/${query ? `?${query}` : ''}`;
  },

  // Customer endpoints
  getCustomersCount: () => `${baseURL}/api/customers/count/`,
  getCustomers: (
    page?: number,
    pageSize?: number,
    ordering?: string,
    filters?: {
      phone?: string;
      search?: string;
      store_id?: string | number;
    },
  ) => {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (pageSize) params.append('page_size', pageSize.toString());
    if (ordering) params.append('ordering', ordering);
    if (filters?.phone) params.append('phone', filters.phone);
    if (filters?.search) params.append('q', filters.search);
    const storeId = filters?.store_id ?? getCurrentStoreId();
    if (storeId) params.append('store_id', String(storeId));
    const query = params.toString();
    return `${baseURL}/api/customers/${query ? `?${query}` : ''}`;
  },

  // Payment endpoints
  createCheckoutSession: () => `${baseURL}/payment/checkout-session`,

  // Communication endpoints
  sendSMS: () => `${baseURL}/twilio/sms`,
  getCalls: (
    page: number = 1,
    pageSize: number = 20,
    completedOnly = true,
    fromNumber?: string,
    ordering?: string,
    filters?: {
      phone?: string;
      search?: string;
      callId?: string;
    },
  ) => {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (pageSize) params.append('page_size', pageSize.toString());
    if (completedOnly) params.append('completed_only', '1');
    if (fromNumber) params.append('from_number', fromNumber);
    if (ordering) params.append('ordering', ordering);
    if (filters?.phone) params.append('phone', filters.phone);
    if (filters?.search) params.append('q', filters.search);
    if (filters?.callId) params.append('call_id', filters.callId);
    const query = params.toString();
    return `${baseURL}/api/calls/${query ? `?${query}` : ''}`;
  },

  getAdminSentNotifications: (
    page?: number,
    pageSize?: number,
    ordering?: string,
    filters?: {
      search?: string;
    },
  ) => {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (pageSize) params.append('page_size', pageSize.toString());
    if (ordering) params.append('ordering', ordering);
    if (filters?.search) params.append('q', filters.search);
    const query = params.toString();
    return `${baseURL}/api/admin/notification-messages${query ? `?${query}` : ''}`;
  },

  getAdminCalls: (
    page?: number,
    pageSize?: number,
    ordering?: string,
    q?: string,
  ) => {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (pageSize) params.append('page_size', pageSize.toString());
    if (ordering) params.append('ordering', ordering);
    if (q) params.append('q', q);
    const query = params.toString();
    return `${baseURL}/api/calls/admin/${query ? `?${query}` : ''}`;
  },

  getAdminReservations: (
    page?: number,
    pageSize?: number,
    ordering?: string,
    filters?: {
      phone?: string;
    },
  ) => {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (pageSize) params.append('page_size', pageSize.toString());
    if (ordering) params.append('ordering', ordering);
    if (filters?.phone) params.append('phone', filters.phone);
    const query = params.toString();
    return `${baseURL}/api/opentable/dashboard/reservations/admin${query ? `?${query}` : ''}`;
  },

  // POS endpoints
  createPOSOrder: () => `${baseURL}/pos/order/`,

  // Item endpoints
  getItems: (status: string) => `${baseURL}/api/item/${status}/`,
  updateItem: (itemId: string) => `${baseURL}/api/item/${itemId}/`,
  updatePOSItem: (itemId: string) => `${baseURL}/pos/item/${itemId}/`,
  getMenuAvailability: () => `${baseURL}/api/menu/availability/`,
  updateMenuAvailabilityBulk: () => `${baseURL}/api/menu/availability/bulk/`,

  // Staff endpoints
  createStaff: () => `${baseURL}/api/create-staff/`,

  // Store endpoints (Platform Admin)
  createStore: () => `${baseURL}/api/stores/`,
  // Legacy path used on older deployments
  createStoreLegacy: () => `${baseURL}/api/stores/store/`,
  getAllStores: (
    page?: number,
    pageSize?: number,
    ordering?: string,
    search?: string,
  ) => {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (pageSize) params.append('page_size', pageSize.toString());
    if (ordering) params.append('ordering', ordering);
    if (search) params.append('search', search);
    const query = params.toString();
    return `${baseURL}/api/stores/${query ? `?${query}` : ''}`;
  },
  getAllStoresUnpaginated: () => `${baseURL}/api/stores/?all=true`,
  getStoreGroupScopeStores: () =>
    `${baseURL}/api/stores/?all=true&fields=group_scope`,
  getStoreById: (id: number | string) => `${baseURL}/api/stores/${id}/`,
  getReservationLocationScope: (id: number | string) =>
    `${baseURL}/api/stores/${id}/reservation-location-scope/`,
  updateReservationLocationScope: (id: number | string) =>
    `${baseURL}/api/stores/${id}/reservation-location-scope/`,
  deleteReservationLocationScope: (
    id: number | string,
    syncPeerScopes = true,
  ) =>
    `${baseURL}/api/stores/${id}/reservation-location-scope/?sync_peer_scopes=${
      syncPeerScopes ? 'true' : 'false'
    }`,
  updateStore: (id: number | string) => `${baseURL}/api/stores/${id}/`,
  updateOwnStore: (id: number | string) => `${baseURL}/api/stores/store/${id}/`,

  // Public store endpoints (no auth; implement server-side)
  getPublicStore: (storeUuid: string | number) =>
    `${baseURL}/api/public/stores/${storeUuid}/`,

  submitPublicOnboardingIntake: () =>
    `${baseURL}/api/public/onboarding-intakes/`,
  // Shared intake links (group onboarding): public, keyed by the link token.
  publicOnboardingLink: (token: string) =>
    `${baseURL}/api/public/onboarding-links/${encodeURIComponent(token)}/`,
  publicOnboardingLinkSubmit: (token: string) =>
    `${baseURL}/api/public/onboarding-links/${encodeURIComponent(token)}/submit/`,
  publicOnboardingLinkFiles: (token: string) =>
    `${baseURL}/api/public/onboarding-links/${encodeURIComponent(token)}/files/`,
  publicOnboardingLinkFile: (token: string, fileId: number | string) =>
    `${baseURL}/api/public/onboarding-links/${encodeURIComponent(token)}/files/${fileId}/`,
  createOnboardingLink: () => `${baseURL}/api/stores/onboarding-intakes/links/`,
  revokeOnboardingLink: (id: number | string) =>
    `${baseURL}/api/stores/onboarding-intakes/${id}/revoke/`,
  replaceOnboardingLink: (id: number | string) =>
    `${baseURL}/api/stores/onboarding-intakes/${id}/new-link/`,
  groupOnboarding: (groupId: number | string) =>
    `${baseURL}/api/stores/store-groups/${groupId}/onboarding/`,

  // Store Group endpoints (Platform Admin)
  getStoreGroups: () => `${baseURL}/api/stores/store-groups/`,
  createStoreGroup: () => `${baseURL}/api/stores/store-groups/`,
  updateStoreGroup: (id: number | string) =>
    `${baseURL}/api/stores/store-groups/${id}/`,

  getVipContacts: (query?: {
    scope: 'store' | 'group';
    store_group_id?: number;
    q?: string;
    page?: number;
    page_size?: number;
    ordering?: string;
  }) => {
    const params = new URLSearchParams();
    if (query?.scope) params.set('scope', query.scope);
    if (query?.store_group_id)
      params.set('store_group_id', String(query.store_group_id));
    if (query?.q) params.set('q', query.q);
    if (query?.page) params.set('page', String(query.page));
    if (query?.page_size) params.set('page_size', String(query.page_size));
    if (query?.ordering) params.set('ordering', query.ordering);
    const qs = params.toString();
    return `${baseURL}/api/stores/vip-contacts/${qs ? `?${qs}` : ''}`;
  },
  createVipContact: () => `${baseURL}/api/stores/vip-contacts/`,
  importVipContacts: () => `${baseURL}/api/stores/vip-contacts/import/`,
  updateVipContact: (id: number | string) =>
    `${baseURL}/api/stores/vip-contacts/${id}/`,
  deleteVipContact: (id: number | string) =>
    `${baseURL}/api/stores/vip-contacts/${id}/`,

  // Onboarding intake endpoints (Platform Admin)
  getOnboardingIntakes: (query?: {
    search?: string;
    status?: string;
    store_id?: string | number;
    ordering?: string;
  }) => {
    const params = new URLSearchParams();
    if (query?.search) params.append('search', query.search);
    if (query?.status) params.append('status', query.status);
    if (query?.store_id) params.append('store_id', String(query.store_id));
    if (query?.ordering) params.append('ordering', query.ordering);
    const qs = params.toString();
    return `${baseURL}/api/stores/onboarding-intakes/${qs ? `?${qs}` : ''}`;
  },
  getOnboardingIntake: (id: number | string) =>
    `${baseURL}/api/stores/onboarding-intakes/${id}/`,
  updateOnboardingIntake: (id: number | string) =>
    `${baseURL}/api/stores/onboarding-intakes/${id}/`,
  linkOnboardingIntakeStore: (id: number | string) =>
    `${baseURL}/api/stores/onboarding-intakes/${id}/link-store/`,
  getOnboardingIntakePrefill: (
    id: number | string,
    target: 'store' | 'owner_user' | 'secondary_profile' | 'tfv' | 'all',
  ) =>
    `${baseURL}/api/stores/onboarding-intakes/${id}/prefill/?target=${target}`,
  getOnboardingIntakeFiles: (intakeId: number | string) =>
    `${baseURL}/api/stores/onboarding-intakes/${intakeId}/files/`,
  uploadOnboardingIntakeFiles: (intakeId: number | string) =>
    `${baseURL}/api/stores/onboarding-intakes/${intakeId}/files/`,
  downloadOnboardingIntakeFile: (
    intakeId: number | string,
    fileId: number | string,
  ) =>
    `${baseURL}/api/stores/onboarding-intakes/${intakeId}/files/${fileId}/download/`,

  // Store User endpoints (Platform Admin)
  createStoreUser: () => `${baseURL}/api/stores/store-user/`,
  getStoreUsers: () => `${baseURL}/api/stores/store-user/`,
  getAdminStoreUsers: (
    page?: number,
    pageSize?: number,
    ordering?: string,
    search?: string,
  ) => {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (pageSize) params.append('page_size', pageSize.toString());
    if (ordering) params.append('ordering', ordering);
    if (search) params.append('search', search);
    const query = params.toString();
    return `${baseURL}/api/stores/store-user/admin/${query ? `?${query}` : ''}`;
  },
  updateStoreUser: (id: number | string) =>
    `${baseURL}/api/stores/store-user/${id}/`,
  // Store memberships (the current store's team)
  getStoreStaff: () => `${baseURL}/api/stores/store-staff/`,
  updateStoreStaff: (id: number | string) =>
    `${baseURL}/api/stores/store-staff/${id}/`,
  getStoreGroupUsers: (storeGroupId?: number | string) =>
    `${baseURL}/api/stores/store-group-users/${storeGroupId ? `?store_group_id=${storeGroupId}` : ''}`,
  createStoreGroupUser: () => `${baseURL}/api/stores/store-group-users/`,
  updateStoreGroupUser: (id: number | string) =>
    `${baseURL}/api/stores/store-group-users/${id}/`,

  // Assistant endpoints (Platform Admin)
  createAssistant: () => `${baseURL}/api/assistants/`,
  getAssistants: (
    page?: number,
    pageSize?: number,
    ordering?: string,
    search?: string,
  ) => {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (pageSize) params.append('page_size', pageSize.toString());
    if (ordering) params.append('ordering', ordering);
    if (search) params.append('search', search);
    const query = params.toString();
    return `${baseURL}/api/assistants/${query ? `?${query}` : ''}`;
  },
  getAssistantById: (id: number | string) => `${baseURL}/api/assistants/${id}/`,
  updateAssistant: (id: number | string) => `${baseURL}/api/assistants/${id}/`,
  getRetellAgents: () => `${baseURL}/api/retell/agents/`,
  getRetellKnowledgeBases: () => `${baseURL}/api/retell/knowledge-bases/`,
  getApprovedUnassignedPhoneNumbers: () =>
    `${baseURL}/api/twilio/approved-unassigned-phone-numbers/`,

  // TSV endpoints (Platform Admin)
  submitTsv: () => `${baseURL}/api/submit-tsv/`,
  getTollFreeVerifications: () => `${baseURL}/api/toll-free-verifications/`,
  refreshTollFreeStatus: () => `${baseURL}/api/refresh-toll-free-status/`,
  getTfvPrefill: () => `${baseURL}/api/store/tfv/prefill/`,
  submitTfv: () => `${baseURL}/api/store/tfv/submit/`,
  getAvailableTwilioPhoneNumbers: () =>
    `${baseURL}/api/twilio/available-phone-numbers/`,
  getTwilioSecondaryCustomerProfiles: () =>
    `${baseURL}/api/twilio/secondary-customer-profiles/`,
  searchTollFreeNumbers: (limit?: number) =>
    `${baseURL}/api/twilio/search-toll-free-numbers/${
      limit ? `?limit=${limit}` : ''
    }`,
  purchasePhoneNumber: () => `${baseURL}/api/twilio/purchase-phone-number/`,
  getPhoneNumberRegulatoryStatuses: (
    page?: number,
    pageSize?: number,
    search?: string,
    ordering?: string,
  ) => {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (pageSize) params.append('page_size', pageSize.toString());
    if (search) params.append('search', search);
    if (ordering) params.append('ordering', ordering);
    const query = params.toString();
    return `${baseURL}/api/twilio/phone-number-regulatory-statuses/${query ? `?${query}` : ''}`;
  },
  deleteTollFreeVerification: (verificationSid: string) =>
    `${baseURL}/api/twilio/toll-free-verifications/${encodeURIComponent(
      verificationSid,
    )}/`,

  // Twilio configuration endpoint (Platform Admin)
  configureTwilio: () => `${baseURL}/api/configure-twilio/`,
  getTwilioSecondaryProfile: () =>
    `${baseURL}/api/store/twilio-secondary-profile/`,
  createTwilioSecondaryProfile: () =>
    `${baseURL}/api/store/twilio-secondary-profile/`,
  syncTwilioSecondaryProfile: () =>
    `${baseURL}/api/store/twilio-secondary-profile/sync/`,
  submitTwilioSecondaryProfile: () =>
    `${baseURL}/api/store/twilio-secondary-profile/submit/`,

  // Knowledge base source files (Platform Admin; scoped by X-Store-ID)
  getKnowledgeBaseSourceFiles: (query?: {
    page?: number;
    page_size?: number;
    category?: string;
    scope?: string;
    status?: string;
    include_in_compilation?: boolean;
    search?: string;
    include_archived?: boolean;
    ordering?: string;
  }) => {
    const params = new URLSearchParams();
    if (query?.page) params.set('page', String(query.page));
    if (query?.page_size) params.set('page_size', String(query.page_size));
    if (query?.category) params.set('category', query.category);
    if (query?.scope) params.set('scope', query.scope);
    if (query?.status) params.set('status', query.status);
    if (query?.include_in_compilation != null) {
      params.set(
        'include_in_compilation',
        query.include_in_compilation ? 'true' : 'false',
      );
    }
    if (query?.search) params.set('search', query.search);
    if (query?.include_archived) params.set('include_archived', '1');
    if (query?.ordering) params.set('ordering', query.ordering);
    const q = params.toString();
    return `${baseURL}/api/knowledge-base/source-files/${q ? `?${q}` : ''}`;
  },
  getKnowledgeBaseSourceFile: (id: number | string) =>
    `${baseURL}/api/knowledge-base/source-files/${id}/`,
  updateKnowledgeBaseSourceFile: (id: number | string) =>
    `${baseURL}/api/knowledge-base/source-files/${id}/`,
  archiveKnowledgeBaseSourceFile: (id: number | string) =>
    `${baseURL}/api/knowledge-base/source-files/${id}/archive/`,
  unarchiveKnowledgeBaseSourceFile: (id: number | string) =>
    `${baseURL}/api/knowledge-base/source-files/${id}/unarchive/`,
  downloadKnowledgeBaseSourceFile: (id: number | string) =>
    `${baseURL}/api/knowledge-base/source-files/${id}/download/`,
  previewKnowledgeBaseSourceFile: (id: number | string) =>
    `${baseURL}/api/knowledge-base/source-files/${id}/download/?disposition=inline`,
  reprocessKnowledgeBaseSourceFile: (id: number | string) =>
    `${baseURL}/api/knowledge-base/source-files/${id}/reprocess/`,
  recrawlKnowledgeBaseSourceFile: (id: number | string) =>
    `${baseURL}/api/knowledge-base/source-files/${id}/recrawl/`,
  deleteKnowledgeBaseSourceFile: (id: number | string) =>
    `${baseURL}/api/knowledge-base/source-files/${id}/`,
  createKnowledgeBaseWebsiteCrawl: () =>
    `${baseURL}/api/knowledge-base/website-crawls/`,
  getKnowledgeBaseWebsiteCrawl: (id: number | string) =>
    `${baseURL}/api/knowledge-base/website-crawls/${id}/`,
  cancelKnowledgeBaseWebsiteCrawl: (id: number | string) =>
    `${baseURL}/api/knowledge-base/website-crawls/${id}/cancel/`,
  getKnowledgeBaseWebsiteCrawlResources: (
    id: number | string,
    query?: { kind?: WebsiteDiscoveredResourceKind; page?: number },
  ) => {
    const params = new URLSearchParams();
    if (query?.kind) params.set('kind', query.kind);
    if (query?.page) params.set('page', String(query.page));
    const q = params.toString();
    return `${baseURL}/api/knowledge-base/website-crawls/${id}/resources/${q ? `?${q}` : ''}`;
  },
  importKnowledgeBaseWebsiteCrawl: (id: number | string) =>
    `${baseURL}/api/knowledge-base/website-crawls/${id}/import/`,
  getKnowledgeBaseWebsiteImportJob: (id: number | string) =>
    `${baseURL}/api/knowledge-base/website-import-jobs/${id}/`,
  cancelKnowledgeBaseWebsiteImportJob: (id: number | string) =>
    `${baseURL}/api/knowledge-base/website-import-jobs/${id}/cancel/`,
  getKnowledgeBaseWebsiteRecrawlJob: (id: number | string) =>
    `${baseURL}/api/knowledge-base/website-recrawl-jobs/${id}/`,
  cancelKnowledgeBaseWebsiteRecrawlJob: (id: number | string) =>
    `${baseURL}/api/knowledge-base/website-recrawl-jobs/${id}/cancel/`,
  getKnowledgeBaseDashboardCompile: () =>
    `${baseURL}/api/knowledge-base/dashboard/compile/`,
  startKnowledgeBaseDashboardCompile: () =>
    `${baseURL}/api/knowledge-base/dashboard/compile/`,
  getKnowledgeBaseCompilationJobs: (query?: { status?: string }) => {
    const params = new URLSearchParams();
    if (query?.status) params.set('status', query.status);
    const q = params.toString();
    return `${baseURL}/api/knowledge-base/compilation-jobs/${q ? `?${q}` : ''}`;
  },
  getKnowledgeBaseArtifact: (id: number | string) =>
    `${baseURL}/api/knowledge-base/artifacts/${id}/`,
  downloadKnowledgeBaseArtifact: (id: number | string) =>
    `${baseURL}/api/knowledge-base/artifacts/${id}/download/`,
  exportKnowledgeBaseArtifactDocx: (id: number | string) =>
    `${baseURL}/api/knowledge-base/artifacts/${id}/export-docx/`,
  submitKnowledgeBaseArtifactFeedback: (id: number | string) =>
    `${baseURL}/api/knowledge-base/artifacts/${id}/feedback/`,

  // Onboarding endpoints (Platform Admin)
  getOnboardingStatus: () => `${baseURL}/api/store/onboarding-status/`,

  // Onboarding checklist: the current store, or a given store for admins
  onboardingChecklist: (storeId?: number | string) =>
    storeId === undefined
      ? `${baseURL}/api/store/onboarding-checklist/`
      : `${baseURL}/api/store/${storeId}/onboarding-checklist/`,
  onboardingChecklistItem: (key: string, storeId?: number | string) =>
    storeId === undefined
      ? `${baseURL}/api/store/onboarding-checklist/items/${key}/`
      : `${baseURL}/api/store/${storeId}/onboarding-checklist/items/${key}/`,
  onboardingTest: () => `${baseURL}/api/store/onboarding-checklist/test/`,
  onboardingTestFeedback: () =>
    `${baseURL}/api/store/onboarding-checklist/test/feedback/`,
  onboardingHoursLookup: () =>
    `${baseURL}/api/store/onboarding-checklist/hours/lookup/`,
  onboardingDocuments: () =>
    `${baseURL}/api/store/onboarding-checklist/documents/`,
  onboardingFaqUrl: () =>
    `${baseURL}/api/store/onboarding-checklist/documents/faq-url/`,
  onboardingDocument: (id: number) =>
    `${baseURL}/api/store/onboarding-checklist/documents/${id}/`,
  onboardingForwarding: () =>
    `${baseURL}/api/store/onboarding-checklist/forwarding/`,
  onboardingStripe: () => `${baseURL}/api/store/onboarding-checklist/stripe/`,

  // Reservation endpoints
  getReservations: (page?: number, pageSize?: number, ordering?: string) => {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (pageSize) params.append('page_size', pageSize.toString());
    if (ordering) params.append('ordering', ordering);
    const query = params.toString();
    return `${baseURL}/api/reservations/${query ? `?${query}` : ''}`;
  },

  // Agent settings endpoints
  getAgentSettings: (userId: number) =>
    `${baseURL}/api/agent-settings/${userId}/`,
  updateAgentSettings: (userId: number) =>
    `${baseURL}/api/agent-settings/${userId}/`,

  // Agent usage endpoints
  getAgentUsageMinutes: () => `${baseURL}/api/agent-usage/minutes/`,

  // Billing / usage endpoints
  getUsageSummary: () => `${baseURL}/api/billing/usage-summary/`,
  getBillingPeriods: () => `${baseURL}/api/billing/periods/`,
  getBillingInvoices: () => `${baseURL}/api/billing/invoices/`,
  getBillingSubscription: () => `${baseURL}/api/billing/subscription/`,
  getAdminSubscriptions: () => `${baseURL}/api/billing/admin/subscriptions/`,
  getAdminBillingSummary: () => `${baseURL}/api/billing/admin/billing-summary/`,
  getAdminUnbilledUsage: () => `${baseURL}/api/billing/admin/unbilled-usage/`,
  createBillingPortalSession: () => `${baseURL}/api/billing/portal/`,
  getPlans: () => `${baseURL}/api/billing/plans/`,

  // Group billing endpoints (explicit group id; owner/admin only)
  getGroupSubscription: (groupId: number | string) =>
    `${baseURL}/api/billing/group/${groupId}/subscription/`,
  getGroupSubscriptionPreviewChange: (
    groupId: number | string,
    planCode: string,
  ) =>
    `${baseURL}/api/billing/group/${groupId}/subscription/preview-change/?plan_code=${encodeURIComponent(planCode)}`,
  createGroupBillingPortalSession: (groupId: number | string) =>
    `${baseURL}/api/billing/group/${groupId}/portal/`,
  getGroupPlans: (groupId: number | string) =>
    `${baseURL}/api/billing/group/${groupId}/plans/`,
  getGroupUsageSummary: (groupId: number | string) =>
    `${baseURL}/api/billing/group/${groupId}/usage-summary/`,
  getGroupInvoices: (groupId: number | string) =>
    `${baseURL}/api/billing/group/${groupId}/invoices/`,
  getGroupBillingPeriods: (groupId: number | string) =>
    `${baseURL}/api/billing/group/${groupId}/periods/`,
  getGroupAddons: (groupId: number | string) =>
    `${baseURL}/api/billing/group/${groupId}/addons/`,

  // Public Activation Endpoints
  getPublicActivationPlans: (country?: string, storeUuid?: string) => {
    const params = [];
    if (country) params.push(`country=${country}`);
    if (storeUuid) params.push(`store_uuid=${storeUuid}`);
    return `${baseURL}/api/public/activation/plans/${params.length ? `?${params.join('&')}` : ''}`;
  },
  getPublicActivationOffer: (token: string) =>
    `${baseURL}/api/public/activation/offers/${token}/`,
  acceptPublicActivationOffer: (token: string) =>
    `${baseURL}/api/public/activation/offers/${token}/accept/`,
  acceptPublicStandardActivation: () =>
    `${baseURL}/api/public/activation/standard/accept/`,
  getPublicActivationOfferOnboardingPrefill: (token: string) =>
    `${baseURL}/api/public/activation/offers/${token}/onboarding-prefill/`,

  // Platform Admin Activation Offer Endpoints
  getBillingActivationOffers: () => `${baseURL}/api/billing/activation-offers/`,
  createBillingActivationOffer: () =>
    `${baseURL}/api/billing/activation-offers/`,
  updateBillingActivationOffer: (id: number | string) =>
    `${baseURL}/api/billing/activation-offers/${id}/`,
  startBillingActivationOfferSubscription: (id: number | string) =>
    `${baseURL}/api/billing/activation-offers/${id}/start-subscription/`,

  // OpenTable endpoints
  openTableStartAuth: () => `${baseURL}/api/opentable/start-auth`,
  openTableAuthorize: () => `${baseURL}/api/opentable/authorize`,
  openTableDashboardReservations: () =>
    `${baseURL}/api/opentable/dashboard/reservations`,
  openTableDashboardDiningAreas: () =>
    `${baseURL}/api/opentable/dashboard/dining-areas/`,
  openTableDashboardAreaBlackouts: (query?: {
    starts_after?: string;
    ends_before?: string;
  }) => {
    const params = new URLSearchParams();
    if (query?.starts_after) params.append('starts_after', query.starts_after);
    if (query?.ends_before) params.append('ends_before', query.ends_before);
    const qs = params.toString();
    return `${baseURL}/api/opentable/dashboard/area-blackouts/${qs ? `?${qs}` : ''}`;
  },
  openTableDashboardAreaBlackoutDetail: (id: number | string) =>
    `${baseURL}/api/opentable/dashboard/area-blackouts/${id}/`,

  // Special event inquiry dashboard endpoints
  getEventInquiries: (query?: {
    page?: number;
    page_size?: number;
    status?: string;
    dashboard_state?: 'unseen' | 'seen' | 'hidden' | 'all';
    search?: string;
    ordering?: string;
    /** Created in this calendar month (`current`) or a `YYYY-MM`. */
    month?: string;
  }) => {
    const params = new URLSearchParams();
    if (query?.page) params.set('page', String(query.page));
    if (query?.page_size) params.set('page_size', String(query.page_size));
    if (query?.status) params.set('status', query.status);
    if (query?.dashboard_state) {
      params.set('dashboard_state', query.dashboard_state);
    }
    if (query?.month) params.set('month', query.month);
    if (query?.search) params.set('search', query.search);
    if (query?.ordering) params.set('ordering', query.ordering);
    const q = params.toString();
    return `${baseURL}/api/event-inquiries/${q ? `?${q}` : ''}`;
  },
  getEventInquiryDetail: (id: number | string) =>
    `${baseURL}/api/event-inquiries/${id}/`,
  updateEventInquiry: (id: number | string) =>
    `${baseURL}/api/event-inquiries/${id}/`,
  getEventInquiryDashboardSummary: () =>
    `${baseURL}/api/event-inquiries/dashboard-summary/`,

  getCateringInquiries: (query?: {
    page?: number;
    page_size?: number;
    status?: string;
    dashboard_state?: 'unseen' | 'seen' | 'hidden' | 'all';
    search?: string;
    ordering?: string;
  }) => {
    const params = new URLSearchParams();
    if (query?.page) params.set('page', String(query.page));
    if (query?.page_size) params.set('page_size', String(query.page_size));
    if (query?.status) params.set('status', query.status);
    if (query?.dashboard_state) {
      params.set('dashboard_state', query.dashboard_state);
    }
    if (query?.search) params.set('search', query.search);
    if (query?.ordering) params.set('ordering', query.ordering);
    const q = params.toString();
    return `${baseURL}/api/catering-inquiries/${q ? `?${q}` : ''}`;
  },
  getCateringInquiryDetail: (id: number | string) =>
    `${baseURL}/api/catering-inquiries/${id}/`,
  updateCateringInquiry: (id: number | string) =>
    `${baseURL}/api/catering-inquiries/${id}/`,
  getCateringInquiryDashboardSummary: () =>
    `${baseURL}/api/catering-inquiries/dashboard-summary/`,
  getCateringLinks: (query?: {
    page?: number;
    pageSize?: number;
    ordering?: string;
  }) => {
    const params = new URLSearchParams();
    if (query?.page) params.append('page', String(query.page));
    if (query?.pageSize) params.append('page_size', String(query.pageSize));
    if (query?.ordering) params.append('ordering', query.ordering);
    const q = params.toString();
    return `${baseURL}/api/catering-links/${q ? `?${q}` : ''}`;
  },
  // Inquiry lists with the filters the API reads (q, processing_status,
  // dashboard_state, page, page_size, ordering).
  eventInquiryList: (query: Record<string, string>) => {
    const q = new URLSearchParams(query).toString();
    return `${baseURL}/api/event-inquiries/${q ? `?${q}` : ''}`;
  },
  eventInquiryDashboardState: (id: number | string) =>
    `${baseURL}/api/event-inquiries/${id}/dashboard-state/`,
  eventInquiryMarkAllRead: () =>
    `${baseURL}/api/event-inquiries/mark-all-read/`,
  cateringInquiryList: (query: Record<string, string>) => {
    const q = new URLSearchParams(query).toString();
    return `${baseURL}/api/catering-inquiries/${q ? `?${q}` : ''}`;
  },
  cateringInquiryDashboardState: (id: number | string) =>
    `${baseURL}/api/catering-inquiries/${id}/dashboard-state/`,
  cateringInquiryMarkAllRead: () =>
    `${baseURL}/api/catering-inquiries/mark-all-read/`,

  // Password reset endpoints
  forgotPassword: () => `${baseURL}/api/auth/password/forgot/`,
  validatePasswordReset: () => `${baseURL}/api/auth/password/reset/validate/`,
  resetPassword: () => `${baseURL}/api/auth/password/reset/`,

  // Notification endpoints
  getNotificationTargets: (storeId?: number | string) =>
    `${baseURL}/api/notification-targets${storeId ? `?store_id=${storeId}` : ''}`,
  getNotificationTargetById: (id: number | string) =>
    `${baseURL}/api/notification-targets/${id}`,
  createNotificationTarget: () => `${baseURL}/api/notification-targets`,
  updateNotificationTarget: (id: number | string) =>
    `${baseURL}/api/notification-targets/${id}`,
  deleteNotificationTarget: (id: number | string) =>
    `${baseURL}/api/notification-targets/${id}`,

  getNotificationRules: (storeId?: number | string) =>
    `${baseURL}/api/notification-rules${storeId ? `?store_id=${storeId}` : ''}`,
  getNotificationEventTypes: (
    storeId?: number | string,
    options?: { fullCatalog?: boolean },
  ) => {
    const params = new URLSearchParams();
    if (storeId) params.set('store_id', String(storeId));
    if (options?.fullCatalog) params.set('catalog', 'full');
    const query = params.toString();
    return `${baseURL}/api/notification-event-types${query ? `?${query}` : ''}`;
  },
  getNotificationRuleById: (id: number | string) =>
    `${baseURL}/api/notification-rules/${id}`,
  createNotificationRule: () => `${baseURL}/api/notification-rules`,
  updateNotificationRule: (id: number | string) =>
    `${baseURL}/api/notification-rules/${id}`,
  deleteNotificationRule: (id: number | string) =>
    `${baseURL}/api/notification-rules/${id}`,

  // Integrations (PI) — orders & reservations status
  getOrdersIntegrationStatus: () =>
    `${baseURL}/api/pi/integrations/orders/status/`,
  getReservationsIntegrationStatus: () =>
    `${baseURL}/api/pi/integrations/reservations/status/`,
  /** Start POS Hub OAuth (store owner/manager; X-Store-ID for platform admins). */
  startPosHubConnect: () => `${baseURL}/api/integrations/poshub/connect/`,
  getPosHubAuthAttemptLinkOptions: (id: number | string) =>
    `${baseURL}/api/integrations/poshub/auth-attempts/${id}/link-options/`,
  linkPosHubAuthAttemptStore: (id: number | string) =>
    `${baseURL}/api/integrations/poshub/auth-attempts/${id}/link-store/`,
  /** Platform admin / Django superuser manual POS Hub setup (JSON body includes store_id). */
  poshubManualSetup: () => `${baseURL}/api/integrations/poshub/manual-setup/`,
  /** Platform admin / superuser POS Hub account + location provisioning. */
  provisionPosHubLocation: () =>
    `${baseURL}/api/integrations/poshub/provision-location/`,

  // Refund Review endpoints (Platform Admin)
  getRefundReviews: (
    page?: number,
    pageSize?: number,
    reviewed?: string,
    storeId?: number,
    ordering?: string,
    q?: string,
  ) => {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (pageSize) params.append('page_size', pageSize.toString());
    if (reviewed) params.append('reviewed', reviewed);
    if (storeId) params.append('store_id', storeId.toString());
    if (ordering) params.append('ordering', ordering);
    if (q) params.append('q', q);
    const query = params.toString();
    return `${baseURL}/api/internal/refund-review/${query ? `?${query}` : ''}`;
  },
  getRefundReviewDetail: (orderId: number) =>
    `${baseURL}/api/internal/refund-review/${orderId}/`,
  markRefundReviewed: (orderId: number) =>
    `${baseURL}/api/internal/refund-review/${orderId}/mark-reviewed/`,
  issueRefund: (orderId: number) =>
    `${baseURL}/api/internal/refund-review/${orderId}/refund/`,

  // Store info endpoints
  getStoreInfo: () => `${baseURL}/api/store/info/`,
  getStoreAgentVoices: () => `${baseURL}/api/store/agent-voices/`,
  getStoreAgentVoice: () => `${baseURL}/api/store/agent-voice/`,
  updateStoreAgentVoice: () => `${baseURL}/api/store/agent-voice/`,
  getStoreHours: () => `${baseURL}/api/store/hours/`,
  updateStoreHours: () => `${baseURL}/api/store/hours/`,
  getStoreGooglePlace: () => `${baseURL}/api/store/google-place/`,
  searchStoreGooglePlace: () => `${baseURL}/api/store/google-place/search/`,
  updateStoreGooglePlace: () => `${baseURL}/api/store/google-place/`,
  getDashboardCallIntelligence: (scope?: 'all_time' | 'month') =>
    scope && scope !== 'all_time'
      ? `${baseURL}/api/dashboard/call-intelligence/?scope=${scope}`
      : `${baseURL}/api/dashboard/call-intelligence/`,

  // Special hours endpoints
  getSpecialHours: (upcoming?: boolean) =>
    `${baseURL}/api/store/special-hours/${upcoming ? '?upcoming=true' : ''}`,
  createSpecialHours: () => `${baseURL}/api/store/special-hours/`,
  updateSpecialHours: (id: number | string) =>
    `${baseURL}/api/store/special-hours/${id}/`,
  deleteSpecialHours: (id: number | string) =>
    `${baseURL}/api/store/special-hours/${id}/`,

  // SMS inbox (store staff + dashboard JWT; X-Store-ID when platform admin)
  smsConversations: (query?: {
    page?: number;
    page_size?: number;
    status?: string;
    needs_human?: boolean;
    unread?: boolean;
    /** Only archived (`true`) or only not (`false`); omit for both. */
    archived?: boolean;
    search?: string;
    ordering?: string;
    /** Started in this calendar month (`current`) or a `YYYY-MM`. */
    month?: string;
  }) => {
    const params = new URLSearchParams();
    params.set('ordering', query?.ordering ?? '-last_message_at');
    if (query?.page) params.set('page', String(query.page));
    if (query?.page_size) params.set('page_size', String(query.page_size));
    if (query?.status) params.set('status', query.status);
    if (query?.needs_human === true) params.set('needs_human', 'true');
    if (query?.unread === true) params.set('unread', 'true');
    if (query?.archived !== undefined)
      params.set('archived', String(query.archived));
    if (query?.search) params.set('search', query.search);
    if (query?.month) params.set('month', query.month);
    const q = params.toString();
    return `${baseURL}/api/messaging/sms/conversations/${q ? `?${q}` : ''}`;
  },
  smsConversationDetail: (id: number | string) =>
    `${baseURL}/api/messaging/sms/conversations/${id}/`,
  smsConversationMessages: (
    id: number | string,
    query: { before?: number; limit?: number } = {},
  ) => {
    const params = new URLSearchParams();
    if (query.before) params.set('before', String(query.before));
    if (query.limit) params.set('limit', String(query.limit));
    const q = params.toString();
    return `${baseURL}/api/messaging/sms/conversations/${id}/messages/${q ? `?${q}` : ''}`;
  },
  smsConversationSend: (id: number | string) =>
    `${baseURL}/api/messaging/sms/conversations/${id}/send/`,
  smsConversationMarkRead: (id: number | string) =>
    `${baseURL}/api/messaging/sms/conversations/${id}/mark-read/`,
  smsConversationMarkUnread: (id: number | string) =>
    `${baseURL}/api/messaging/sms/conversations/${id}/mark-unread/`,
  smsConversationMarkAllRead: () =>
    `${baseURL}/api/messaging/sms/conversations/mark-all-read/`,
  smsConversationUpdate: (id: number | string) =>
    `${baseURL}/api/messaging/sms/conversations/${id}/update/`,

  // Voicemail summaries (store staff; X-Store-ID when platform admin)
  voicemails: (query?: {
    page?: number;
    page_size?: number;
    folder?: 'inbox' | 'archived' | 'all';
    unread?: boolean;
    /** Left in this calendar month (`current`) or a `YYYY-MM`. */
    month?: string;
  }) => {
    const params = new URLSearchParams();
    if (query?.page) params.set('page', String(query.page));
    if (query?.page_size) params.set('page_size', String(query.page_size));
    if (query?.folder) params.set('folder', query.folder);
    if (query?.unread === true) params.set('unread', 'true');
    if (query?.month) params.set('month', query.month);
    const queryString = params.toString();
    return `${baseURL}/api/voicemails/${queryString ? `?${queryString}` : ''}`;
  },
  voicemailDetail: (id: number | string) => `${baseURL}/api/voicemails/${id}/`,
  voicemailUpdate: (id: number | string) =>
    `${baseURL}/api/voicemails/${id}/update/`,
  voicemailMarkAllRead: () => `${baseURL}/api/voicemails/mark-all-read/`,
  voicemailReply: (id: number | string) =>
    `${baseURL}/api/voicemails/${id}/reply/`,

  // Questions callers asked that Arthur couldn't answer, and the store's
  // Additional FAQs (store staff; publish/archive Owner or Manager)
  knowledgeQuestions: (query?: {
    status?: 'open' | 'answered' | 'dismissed' | 'published' | 'all';
    page?: number;
    page_size?: number;
  }) => {
    const params = new URLSearchParams();
    if (query?.status) params.set('status', query.status);
    if (query?.page) params.set('page', String(query.page));
    if (query?.page_size) params.set('page_size', String(query.page_size));
    const queryString = params.toString();
    return `${baseURL}/api/knowledge-questions/${queryString ? `?${queryString}` : ''}`;
  },
  knowledgeQuestionSummary: () => `${baseURL}/api/knowledge-questions/summary/`,
  knowledgeQuestionDetail: (id: number | string) =>
    `${baseURL}/api/knowledge-questions/${id}/`,
  knowledgeQuestionAnswer: (id: number | string) =>
    `${baseURL}/api/knowledge-questions/${id}/answer/`,
  knowledgeQuestionDismiss: (id: number | string) =>
    `${baseURL}/api/knowledge-questions/${id}/dismiss/`,
  knowledgeQuestionReopen: (id: number | string) =>
    `${baseURL}/api/knowledge-questions/${id}/reopen/`,
  additionalFaqs: (status?: 'active' | 'archived' | 'all') =>
    `${baseURL}/api/additional-faqs/${status ? `?status=${status}` : ''}`,
  additionalFaqSyncStatus: () => `${baseURL}/api/additional-faqs/sync-status/`,
  additionalFaqDetail: (id: number | string) =>
    `${baseURL}/api/additional-faqs/${id}/`,
  additionalFaqCheck: (id: number | string) =>
    `${baseURL}/api/additional-faqs/${id}/check/`,
  additionalFaqPublish: (id: number | string) =>
    `${baseURL}/api/additional-faqs/${id}/publish/`,
  additionalFaqArchive: (id: number | string) =>
    `${baseURL}/api/additional-faqs/${id}/archive/`,
  additionalFaqSupportDraft: (id: number | string) =>
    `${baseURL}/api/additional-faqs/${id}/support-draft/`,
  additionalFaqSupport: (id: number | string) =>
    `${baseURL}/api/additional-faqs/${id}/support/`,
  knowledgeBaseView: () => `${baseURL}/api/knowledge-base-view/`,
  // Flagged Additional FAQs stores sent to support (Platform Admin)
  adminFaqSupport: (query?: {
    status?: 'open' | 'resolved';
    page?: number;
    page_size?: number;
  }) => {
    const params = new URLSearchParams();
    if (query?.status) params.set('status', query.status);
    if (query?.page) params.set('page', String(query.page));
    if (query?.page_size) params.set('page_size', String(query.page_size));
    const queryString = params.toString();
    return `${baseURL}/api/admin/faq-support/${queryString ? `?${queryString}` : ''}`;
  },
  adminFaqSupportDetail: (id: number | string) =>
    `${baseURL}/api/admin/faq-support/${id}/`,
  adminFaqSupportResolve: (id: number | string) =>
    `${baseURL}/api/admin/faq-support/${id}/resolve/`,

  // Sales outbound prospects (Platform Admin)
  getSalesProspects: (query?: {
    page?: number;
    page_size?: number;
    status?: string;
    q?: string;
    ordering?: string;
  }) => {
    const params = new URLSearchParams();
    if (query?.page) params.set('page', String(query.page));
    if (query?.page_size) params.set('page_size', String(query.page_size));
    if (query?.status) params.set('status', query.status);
    if (query?.q) params.set('q', query.q);
    if (query?.ordering) params.set('ordering', query.ordering);
    const q = params.toString();
    return `${baseURL}/api/sales/prospects/${q ? `?${q}` : ''}`;
  },
  createSalesProspect: () => `${baseURL}/api/sales/prospects/`,
  importSalesProspects: () => `${baseURL}/api/sales/prospects/import/`,
  getSalesProspect: (id: number | string) =>
    `${baseURL}/api/sales/prospects/${id}/`,
  updateSalesProspect: (id: number | string) =>
    `${baseURL}/api/sales/prospects/${id}/`,
  startSalesProspectAudit: (id: number | string) =>
    `${baseURL}/api/sales/prospects/${id}/start-audit/`,
  researchSalesProspect: (id: number | string) =>
    `${baseURL}/api/sales/prospects/${id}/research/`,
  getSalesProspectCompetitors: (id: number | string) =>
    `${baseURL}/api/sales/prospects/${id}/competitors/`,
  discoverSalesProspectCompetitors: (id: number | string) =>
    `${baseURL}/api/sales/prospects/${id}/competitors/discover/`,
  generateSalesProspectDrip: (id: number | string) =>
    `${baseURL}/api/sales/prospects/${id}/generate-drip/`,
  renderSalesProspectDripTemplate: (id: number | string) =>
    `${baseURL}/api/sales/prospects/${id}/render-drip-template/`,
  approveSalesProspectDrip: (id: number | string) =>
    `${baseURL}/api/sales/prospects/${id}/approve-drip/`,
  updateSalesProspectDripDraft: (
    prospectId: number | string,
    draftId: number | string,
  ) => `${baseURL}/api/sales/prospects/${prospectId}/drip-drafts/${draftId}/`,
  pushSalesProspectToInstantly: (id: number | string) =>
    `${baseURL}/api/sales/prospects/${id}/push-to-instantly/`,
  markSalesProspectStatus: (id: number | string) =>
    `${baseURL}/api/sales/prospects/${id}/mark-status/`,
  salesConfigCampaignTypes: () => `${baseURL}/api/sales/config/campaign-types/`,
  salesConfigCampaignType: (id: number | string) =>
    `${baseURL}/api/sales/config/campaign-types/${id}/`,
  salesConfigDripTemplates: () => `${baseURL}/api/sales/config/drip-templates/`,
  salesConfigDripTemplate: (id: number | string) =>
    `${baseURL}/api/sales/config/drip-templates/${id}/`,
  salesConfigSignalCategories: () =>
    `${baseURL}/api/sales/config/signal-categories/`,
  salesConfigSignalCategory: (id: number | string) =>
    `${baseURL}/api/sales/config/signal-categories/${id}/`,
  salesConfigPlaybooks: () => `${baseURL}/api/sales/config/playbooks/`,
  salesConfigPlaybook: (id: number | string) =>
    `${baseURL}/api/sales/config/playbooks/${id}/`,
  salesConfigRevenueAssumptions: () =>
    `${baseURL}/api/sales/config/revenue-assumptions/`,
  salesConfigRevenueAssumption: (id: number | string) =>
    `${baseURL}/api/sales/config/revenue-assumptions/${id}/`,
  salesConfigAutomationSettings: () =>
    `${baseURL}/api/sales/config/automation-settings/`,
  platformInsightsChat: () => `${baseURL}/api/stores/platform-insights/chat/`,
  platformInsightsChats: () => `${baseURL}/api/stores/platform-insights/chats/`,
  platformInsightsChatDetail: (chatId: number | string) =>
    `${baseURL}/api/stores/platform-insights/chats/${chatId}/`,
  demoLines: () => `${baseURL}/api/demo-lines/`,
  demoLine: (id: number | string) => `${baseURL}/api/demo-lines/${id}/`,
};
